/**
 * useTeleport - QueBoard용 텔레포트 훅 (3단계 상태 머신)
 *
 * idle → selecting → placing 3단계로 수동 텔레포트 수행.
 * 'on' 상태 셀만 대상, 'done' 셀과 충돌 시 배치 불가.
 *
 * 검증:
 * - 사전: 인형 10개 미만 → 10묶음 불가 안내
 * - 사후: 결과가 10묶음을 만들 수 없으면 안내 메시지
 */
import { useCallback, useRef, useState } from 'react'
import type { TeleportState, UnitData } from '../types'
import { MAP_SIZE } from '../constants'

/* ------------------------------------------------------------------ */
/*  Shared utilities (unchanged)                                       */
/* ------------------------------------------------------------------ */

interface BoundingBox {
  minR: number
  maxR: number
  minC: number
  maxC: number
}

function calcBoundingBox(coords: [number, number][]): BoundingBox {
  if (coords.length === 0) return { minR: MAP_SIZE, maxR: -1, minC: MAP_SIZE, maxC: -1 }
  let minR = MAP_SIZE, maxR = -1, minC = MAP_SIZE, maxC = -1
  for (const [r, c] of coords) {
    if (r < minR) minR = r
    if (r > maxR) maxR = r
    if (c < minC) minC = c
    if (c > maxC) maxC = c
  }
  return { minR, maxR, minC, maxC }
}

/**
 * QueBoard mapGrid (col-major) → row-major 0/1 grid 변환 ('on' 상태만)
 */
function mapGridToRowMajor(mapGrid: UnitData[][]): number[][] {
  const grid: number[][] = Array.from({ length: MAP_SIZE }, () => Array(MAP_SIZE).fill(0))
  for (let col = 0; col < MAP_SIZE; col++) {
    for (let row = 0; row < MAP_SIZE; row++) {
      if (mapGrid[col]?.[row]?.state === 'on') {
        grid[row][col] = 1
      }
    }
  }
  return grid
}

/**
 * 'done' 상태 셀 위치 Set (row,col 형식)
 */
function getDoneCells(mapGrid: UnitData[][]): Set<string> {
  const done = new Set<string>()
  for (let col = 0; col < MAP_SIZE; col++) {
    for (let row = 0; row < MAP_SIZE; row++) {
      if (mapGrid[col]?.[row]?.state === 'done') {
        done.add(`${row},${col}`)
      }
    }
  }
  return done
}

/* ------------------------------------------------------------------ */
/*  New utility functions for 3-phase state machine                    */
/* ------------------------------------------------------------------ */

const INITIAL_STATE: TeleportState = {
  phase: 'idle',
  axis: null,
  targetLength: null,
  recommendedLines: [],
  recommendedCells: [],
  selectedLines: [],
  selectedCells: [],
  ghostCells: [],
  ghostAnchor: null,
}

/**
 * bounding box 내에서 selecting 가능한 줄 범위 계산
 * axis='row' → 행 단위, axis='col' → 열 단위
 * 첫 행/열부터 연속, done 셀 포함 행 직전까지
 */
function getSelectableRange(
  mapGrid: UnitData[][],
  axis: 'row' | 'col',
): { min: number; max: number; onCells: Map<number, { col: number; row: number }[]> } {
  const grid = mapGridToRowMajor(mapGrid)
  const activeBlocks: [number, number][] = []
  for (let r = 0; r < MAP_SIZE; r++) {
    for (let c = 0; c < MAP_SIZE; c++) {
      if (grid[r][c] === 1) activeBlocks.push([r, c])
    }
  }
  if (activeBlocks.length === 0) return { min: 0, max: -1, onCells: new Map() }

  const bb = calcBoundingBox(activeBlocks)
  const doneCells = getDoneCells(mapGrid)

  // 끝(아래/오른쪽)부터 역순으로 선택 가능 범위 계산
  const lineStart = axis === 'row' ? bb.minR : bb.minC
  const lineEnd = axis === 'row' ? bb.maxR : bb.maxC
  const onCells = new Map<number, { col: number; row: number }[]>()

  let selectableMin = lineEnd + 1
  for (let line = lineEnd; line >= lineStart; line--) {
    let hasDone = false
    const cells: { col: number; row: number }[] = []
    if (axis === 'row') {
      for (let c = bb.minC; c <= bb.maxC; c++) {
        if (doneCells.has(`${line},${c}`)) { hasDone = true; break }
        if (grid[line][c] === 1) cells.push({ col: c, row: line })
      }
    } else {
      for (let r = bb.minR; r <= bb.maxR; r++) {
        if (doneCells.has(`${r},${line}`)) { hasDone = true; break }
        if (grid[r][line] === 1) cells.push({ col: line, row: r })
      }
    }
    if (hasDone || cells.length === 0) break
    onCells.set(line, cells)
    selectableMin = line
  }

  return { min: selectableMin, max: lineEnd, onCells }
}

/**
 * 선택 셀 90도 회전 → 상대좌표(0,0 기준) 배열 반환
 */
function rotateSelectedCells(
  selectedCells: { col: number; row: number }[],
  axis: 'row' | 'col',
): { col: number; row: number }[] {
  if (selectedCells.length === 0) return []

  const rows = selectedCells.map(c => c.row)
  const cols = selectedCells.map(c => c.col)
  const minR = Math.min(...rows), maxR = Math.max(...rows)
  const minC = Math.min(...cols), maxC = Math.max(...cols)
  const height = maxR - minR + 1
  const width = maxC - minC + 1

  return selectedCells.map(({ col, row }) => {
    if (axis === 'row') {
      return { col: height - 1 - (row - minR), row: col - minC }
    } else {
      return { col: row - minR, row: width - 1 - (col - minC) }
    }
  })
}

/**
 * placing 유효성 판단 — ghost 셀이 그리드 범위 내이고 on/done과 겹치지 않는지
 */
function validateGhostPlacement(
  ghostCells: { col: number; row: number }[],
  anchor: { col: number; row: number },
  mapGrid: UnitData[][],
): boolean {
  for (const ghost of ghostCells) {
    const absCol = anchor.col + ghost.col
    const absRow = anchor.row + ghost.row
    if (absCol < 0 || absCol >= MAP_SIZE || absRow < 0 || absRow >= MAP_SIZE) return false
    const state = mapGrid[absCol]?.[absRow]?.state
    if (state === 'on' || state === 'done') return false
  }
  return true
}

/**
 * 배치 후 사후 검증 (targetLength=10일 때 줄십 체크)
 */
function checkPostPlacement(
  mapGrid: UnitData[][],
  targetLength: number,
): string | null {
  if (targetLength !== 10) return null

  const grid = mapGridToRowMajor(mapGrid)
  let total = 0
  const posSet = new Set<string>()
  for (let r = 0; r < MAP_SIZE; r++) {
    for (let c = 0; c < MAP_SIZE; c++) {
      if (grid[r][c] === 1) { posSet.add(`${r},${c}`); total++ }
    }
  }

  let autoTiedCount = 0
  for (let r = 0; r < MAP_SIZE; r++) {
    let full = true
    for (let c = 0; c < MAP_SIZE; c++) {
      if (!posSet.has(`${r},${c}`)) { full = false; break }
    }
    if (full) autoTiedCount += MAP_SIZE
  }
  for (let c = 0; c < MAP_SIZE; c++) {
    let full = true
    for (let r = 0; r < MAP_SIZE; r++) {
      if (!posSet.has(`${r},${c}`)) { full = false; break }
    }
    if (full) autoTiedCount += MAP_SIZE
  }

  if (total - autoTiedCount >= 10) return '10묶음을 만들 수 없는 배치입니다'
  return null
}

/* ------------------------------------------------------------------ */
/*  useTeleport hook                                                   */
/* ------------------------------------------------------------------ */

export function useTeleport(
  mapGrid: UnitData[][],
  setMapGridState: (updater: (prev: UnitData[][]) => UnitData[][]) => void,
  setFeedback: (f: { message: string; isCorrect: boolean } | null) => void,
  onCancelReset?: () => void,
) {
  const [state, setState] = useState<TeleportState>(INITIAL_STATE)
  const selectedCellsRef = useRef<{ col: number; row: number }[]>([])
  const rotateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // --- startSelecting: idle → selecting (추천 행 자동 계산 + 즉시 하이라이트) ---
  const startSelecting = useCallback((targetLength: number) => {
    if (state.phase !== 'idle') return

    const grid = mapGridToRowMajor(mapGrid)
    const activeBlocks: [number, number][] = []
    for (let r = 0; r < MAP_SIZE; r++) {
      for (let c = 0; c < MAP_SIZE; c++) {
        if (grid[r][c] === 1) activeBlocks.push([r, c])
      }
    }

    if (activeBlocks.length === 0) {
      setFeedback({ message: '이동할 인형이 없습니다', isCorrect: false })
      return
    }
    if (activeBlocks.length < targetLength) {
      setFeedback({ message: `인형이 ${targetLength}개 미만이라 ${targetLength}묶음을 만들 수 없습니다`, isCorrect: false })
      return
    }

    const bb = calcBoundingBox(activeBlocks)
    const width = bb.maxC - bb.minC + 1
    const height = bb.maxR - bb.minR + 1

    if (width === targetLength) {
      setFeedback({ message: '이미 목표 크기입니다', isCorrect: false })
      return
    }

    // 텔레포트10 사전 검증: 배치 후 낱개 ≥ 10이면 불가능
    if (targetLength === 10 && (10 - width) * (10 - height) >= 10) {
      setFeedback({ message: '10묶음을 만들 수 없는 배치입니다', isCorrect: false })
      return
    }

    // 축 자동 결정: width > target → col(세로줄 제거), width < target → row(가로줄 이동)
    const axis: 'row' | 'col' = width > targetLength ? 'col' : 'row'

    const numLinesToMove = Math.abs(width - targetLength)

    // 선택 가능 범위 조회 (끝에서부터)
    const range = getSelectableRange(mapGrid, axis)
    if (range.max < range.min) {
      setFeedback({ message: '선택 가능한 행이 없습니다', isCorrect: false })
      return
    }

    // 끝에서부터 numLinesToMove 만큼 추천 행 계산 (가이드용)
    const recommendedLines: number[] = []
    const recommendedCells: { col: number; row: number }[] = []
    for (let line = range.max; line >= range.min && recommendedLines.length < numLinesToMove; line--) {
      const cells = range.onCells.get(line)
      if (cells) {
        recommendedLines.push(line)
        recommendedCells.push(...cells)
      }
    }
    recommendedLines.sort((a, b) => a - b)

    if (recommendedCells.length === 0) {
      setFeedback({ message: '선택 가능한 행이 없습니다', isCorrect: false })
      return
    }

    setState({
      phase: 'selecting',
      axis,
      targetLength,
      recommendedLines,
      recommendedCells,
      selectedLines: [],
      selectedCells: [],
      ghostCells: [],
      ghostAnchor: null,
    })
    setFeedback(null)
  }, [state.phase, mapGrid, setFeedback])

  // --- selectLine: 드래그 중 추천 행 하나를 선택 (이미 선택된 행은 무시) ---
  const selectLine = useCallback((lineIndex: number) => {
    if (state.phase !== 'selecting' || !state.axis) return
    // 추천 행이 아니면 무시
    if (!state.recommendedLines.includes(lineIndex)) return
    // 이미 선택된 행이면 무시
    if (state.selectedLines.includes(lineIndex)) return

    // 해당 행의 셀 찾기
    const range = getSelectableRange(mapGrid, state.axis)
    const lineCells = range.onCells.get(lineIndex)
    if (!lineCells) return

    const newSelectedLines = [...state.selectedLines, lineIndex].sort((a, b) => a - b)
    const newSelectedCells = [...state.selectedCells, ...lineCells]

    setState(prev => ({
      ...prev,
      selectedLines: newSelectedLines,
      selectedCells: newSelectedCells,
    }))
  }, [state.phase, state.axis, state.recommendedLines, state.selectedLines, state.selectedCells, mapGrid])

  /** 추천 행이 전부 선택되었는지 확인 */
  const isSelectionComplete = state.phase === 'selecting'
    && state.recommendedLines.length > 0
    && state.recommendedLines.every(l => state.selectedLines.includes(l))

  // --- confirmSelection: selecting → rotating → placing ---
  // recommendedCells를 사용 (startSelecting에서 한 번 계산, 항상 완전한 셀 목록)
  const confirmSelection = useCallback(() => {
    if (state.phase !== 'selecting' || state.recommendedCells.length === 0) return

    // 원위치 복원용 보관 (recommendedCells = 이동할 전체 셀)
    selectedCellsRef.current = [...state.recommendedCells]

    // 회전 후 ghost 좌표 계산
    const ghostCells = rotateSelectedCells(state.recommendedCells, state.axis!)

    // rotating 단계 진입 (애니메이션 표시)
    setState(prev => ({
      ...prev,
      phase: 'rotating',
      selectedCells: state.recommendedCells,
      ghostCells,
    }))

    // 500ms 후 → 셀 제거 + placing 진입 (초기 앵커 설정)
    const cellsToRemove = [...state.recommendedCells]
    rotateTimerRef.current = setTimeout(() => {
      setMapGridState(prev => {
        const next = prev.map(col => col.map(cell => ({ ...cell })))
        for (const { col, row } of cellsToRemove) {
          next[col][row].state = 'off'
        }
        return next
      })

      setState(prev => ({
        ...prev,
        phase: 'placing',
        ghostAnchor: null,
      }))
    }, 500)
  }, [state.phase, state.recommendedCells, state.axis, mapGrid, setMapGridState])

  // --- updateGhostAnchor: placing 중 마우스 추적 ---
  const updateGhostAnchor = useCallback((anchor: { col: number; row: number } | null) => {
    if (state.phase !== 'placing') return

    if (!anchor) {
      setState(prev => ({ ...prev, ghostAnchor: null }))
      return
    }

    // remaining 블록의 bounding box 계산
    const grid = mapGridToRowMajor(mapGrid)
    const remaining: [number, number][] = []
    for (let r = 0; r < MAP_SIZE; r++) {
      for (let c = 0; c < MAP_SIZE; c++) {
        if (grid[r][c] === 1) remaining.push([r, c])
      }
    }

    if (remaining.length > 0) {
      const remBB = calcBoundingBox(remaining)
      if (state.axis === 'row') {
        const fixedAnchor = { col: remBB.maxC + 1, row: remBB.minR }
        // 마우스가 기존 인형 오른쪽에 있고 배치 가능하면 → 정답 위치에 고정
        if (anchor.col > remBB.maxC && validateGhostPlacement(state.ghostCells, fixedAnchor, mapGrid)) {
          setState(prev => ({ ...prev, ghostAnchor: fixedAnchor }))
        } else {
          // 배치 불가 영역: 마우스 따라 이동 (빨간색 표시)
          setState(prev => ({ ...prev, ghostAnchor: { col: anchor.col, row: remBB.minR } }))
        }
      } else {
        const fixedAnchor = { col: remBB.minC, row: remBB.maxR + 1 }
        // 마우스가 기존 인형 아래에 있고 배치 가능하면 → 정답 위치에 고정
        if (anchor.row > remBB.maxR && validateGhostPlacement(state.ghostCells, fixedAnchor, mapGrid)) {
          setState(prev => ({ ...prev, ghostAnchor: fixedAnchor }))
        } else {
          // 배치 불가 영역: 마우스 따라 이동 (빨간색 표시)
          setState(prev => ({ ...prev, ghostAnchor: { col: remBB.minC, row: anchor.row } }))
        }
      }
    } else {
      setState(prev => ({ ...prev, ghostAnchor: anchor }))
    }
  }, [state.phase, state.axis, mapGrid])

  // --- isPlacementValid: placing 유효성 확인 ---
  const isPlacementValid = useCallback((anchor: { col: number; row: number }): boolean => {
    return validateGhostPlacement(state.ghostCells, anchor, mapGrid)
  }, [state.ghostCells, mapGrid])

  // --- confirmPlacement: placing 확정 배치 ---
  const confirmPlacement = useCallback((anchor: { col: number; row: number }) => {
    if (state.phase !== 'placing') return

    if (!validateGhostPlacement(state.ghostCells, anchor, mapGrid)) {
      setFeedback({ message: '여기에는 배치할 수 없습니다', isCorrect: false })
      return
    }

    // 배치 실행
    setMapGridState(prev => {
      const next = prev.map(col => col.map(cell => ({ ...cell })))
      for (const ghost of state.ghostCells) {
        const absCol = anchor.col + ghost.col
        const absRow = anchor.row + ghost.row
        next[absCol][absRow].state = 'on'
      }
      return next
    })

    // 사후 검증 — setMapGridState는 비동기이므로 임시 그리드로 검증
    const tempGrid = mapGrid.map(col => col.map(cell => ({ ...cell })))
    for (const ghost of state.ghostCells) {
      const absCol = anchor.col + ghost.col
      const absRow = anchor.row + ghost.row
      tempGrid[absCol][absRow].state = 'on'
    }
    const warning = checkPostPlacement(tempGrid, state.targetLength ?? 0)

    selectedCellsRef.current = []
    setState(INITIAL_STATE)

    if (warning) {
      setFeedback({ message: warning, isCorrect: false })
    } else {
      setFeedback(null)
    }
  }, [state.phase, state.ghostCells, state.targetLength, mapGrid, setMapGridState, setFeedback])

  // --- cancel: selecting/rotating/placing → idle (리셋과 동일 효과) ---
  const cancel = useCallback(() => {
    if (state.phase === 'idle') return

    if (rotateTimerRef.current) { clearTimeout(rotateTimerRef.current); rotateTimerRef.current = null }
    selectedCellsRef.current = []
    setState(INITIAL_STATE)
    onCancelReset?.()
  }, [state.phase, onCancelReset])

  // --- reset: 전체 리셋 (외부 문제 변경용) ---
  const reset = useCallback(() => {
    if (rotateTimerRef.current) { clearTimeout(rotateTimerRef.current); rotateTimerRef.current = null }
    selectedCellsRef.current = []
    setState(INITIAL_STATE)
  }, [])

  return {
    state,
    startSelecting,
    selectLine,
    isSelectionComplete,
    confirmSelection,
    updateGhostAnchor,
    isPlacementValid,
    confirmPlacement,
    cancel,
    reset,
    isActive: state.phase !== 'idle',
  }
}
